import HomePage from "./pages/HomePage";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import PublicOnlyRoute from "@/components/PublicOnlyRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LandingPage from "./pages/LandingPage";
import NotFound from "./pages/NotFound";
import SignupPage from "./pages/SignupPage";
import SignInPage from "./pages/SignInPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import AuthCallback from "./pages/AuthCallback";
import AuthenticatedLayout from "./layouts/AuthenticatedLayout";
import CourseLayout from "./layouts/CourseLayout";
import SettingsPage from "./pages/SettingsPage";
import AccountInformationPage from "./pages/AccountInformationPage";
import LessonPlayer from "./pages/LessonPlayer";
import UnifiedLearningPage from "./pages/UnifiedLearningPage";
import CoursePage from "./pages/CoursePage";
import ProfilePage from "./pages/ProfilePage";
import MascotTest from "./pages/MascotTest";
import AvatarCustomizationPage from "./pages/AvatarCustomizationPage";
import SimulationPreview from "./pages/SimulationPreview";


const queryClient = new QueryClient();

const basename = '/';

function CourseRedirect() {
  const { slug } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (slug) navigate(`/learning/${slug}`, { replace: true });
  }, [slug, navigate]);

  return null;
}

function AppContent() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div style={{ background: '#0F172A', 
                    width: '100vw', 
                    height: '100vh' }} />
    );
  }

  return (
    <>
      <Toaster />
      <Sonner />
      <ErrorBoundary>
        <BrowserRouter basename={basename}>
          <Routes>
            {/* Public routes (redirect away when already onboarded) */}
            <Route path="/" element={<PublicOnlyRoute><LandingPage /></PublicOnlyRoute>} />
            <Route path="/signup" element={<PublicOnlyRoute><SignupPage /></PublicOnlyRoute>} />
            <Route path="/signin" element={<PublicOnlyRoute><SignInPage /></PublicOnlyRoute>} />
            <Route path="/login" element={<Navigate to="/signin" replace />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<div>Reset password coming soon</div>} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/course/:slug" element={<ProtectedRoute><CourseRedirect /></ProtectedRoute>} />

            {/* Protected routes */}
            <Route path="/dashboard" element={<Navigate to="/home" replace />} />
            <Route path="home" element={<ProtectedRoute><AuthenticatedLayout /></ProtectedRoute>}>
              <Route index element={<HomePage />} />
            </Route>
            <Route path="learning" element={<ProtectedRoute><AuthenticatedLayout /></ProtectedRoute>}>
              <Route index element={<UnifiedLearningPage />} />
            </Route>
            <Route path="learning/:courseSlug" element={<ProtectedRoute><CourseLayout /></ProtectedRoute>}>
              <Route index element={<CoursePage />} />
            </Route>
            <Route path="lesson/:lessonSlug/:pageIndex?" element={<ProtectedRoute><AuthenticatedLayout /></ProtectedRoute>}>
              <Route index element={<LessonPlayer />} />
            </Route>
            <Route path="profile" element={<ProtectedRoute><AuthenticatedLayout /></ProtectedRoute>}>
              <Route index element={<ProfilePage />} />
            </Route>
            <Route path="settings" element={<ProtectedRoute><AuthenticatedLayout /></ProtectedRoute>}>
              <Route index element={<SettingsPage />} />
            </Route>
            <Route path="settings/account" element={<ProtectedRoute><AuthenticatedLayout /></ProtectedRoute>}>
              <Route index element={<AccountInformationPage />} />
            </Route>
            <Route path="avatar" element={<ProtectedRoute><AvatarCustomizationPage /></ProtectedRoute>} />

            {/* Mascot Test Route (Dev-Only, No Auth Required) */}
            {import.meta.env.DEV && (
              <Route path="/mascot-test" element={<MascotTest />} />
            )}

            {/* Simulation Preview Route (Dev-Only, No Auth Required) */}
            {import.meta.env.DEV && (
              <Route path="/simulation-preview" element={<SimulationPreview />} />
            )}


            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
    </>
  );
}

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
